import {ObjectKeys} from '../objectkeys'

// Arguments to a many stat calls
export interface Params {
    location: number
    body: {
        Start: Date
        End: Date
        Meters: number[]
        ShowOtherCards?: boolean
    }
}

export interface ParamsXlsx{
    location: number
    body: {
        Start: Date
        End: Date
        Meters: number[]
        Columns1:object
        Columns2?:object
        Columns3?:object
        ShowOtherCards?: boolean
        ShowAllEvents?:boolean
        TransactionType?:number
        LicenseType?: number
        Areas?: AreaEntry[]
        Status?: StatusEntry[]
        requestType?: string // Should be 'requests' or 'invoicing'. Default is requests
    }
}

// Response data
export interface ExportXlsxData{
    fileName:string
    data: string
}

export interface DeviceListResponse {
    version: string
    id: string
    result: DeviceData[]
}

export class DeviceListResponse implements DeviceListResponse {
    constructor(version: string = "", id: string = "", devices: DeviceData[] = []) {
        this.version = version
        this.id = id
        this.result = devices
    }
}

export interface DeviceData {
    id: number
    name: string
}

export interface PaymentResponse {
    version: string
    id: string
    result: PaymentData[][]
}

export interface PaymentData {
    id: number
    amount: number
    count: string
    deviceName: string
}

export interface CardPaymentData {
    version: string
    id: string
    result: {
        headers: string[]
        rows: Array<string | number>[]

        otherCardHeaders: string[]
        otherCardRows: Array<string | number>[]
    }
}

export class CardPaymentData implements CardPaymentData {
    constructor(version: string = "", id: string = "", headers: string[] = [], rows: (string|number)[][] = [], oHeaders: string[] = [], oRows: (string|number)[][] = []) {
        this.version = version
        this.id = id
        this.result = {headers: headers, rows: rows, otherCardHeaders: oHeaders, otherCardRows: oRows}
    }
}

export interface EmptyingData extends ObjectKeys {
    deviceName: string
    emptyNr: number | string
    emptyTime: Date
    emptyTimeString: string
    amount: number | string
    valueDk001: number | string
    countDk001: number | string
    valueDk002: number | string
    countDk002: number | string
    valueDk005: number | string
    countDk005: number | string
    valueDk010: number | string
    countDk010: number | string
    valueDk020: number | string
    countDk020: number | string
    valueDkEmpty: number | string
    countDkEmpty: number | string
    valueTotal: number | string
}

export interface EmptyingResponse {
    version: string
    id: string
    result: EmptyingData[]
}

export interface TransactionReportData extends ObjectKeys {
    id: string
    deviceName: string
    cardFirm: string
    card: string
    cardCount: number | string
    amount: number | string
    fee: number | string
    paymentTime: Date
    paymentTimeString: string
    parkingTime: number | string
    plate: string
    start: Date | string
    end: Date | string
    rateType: string
    discountCode: string
    discountType: string
}

export interface TransactionReportResponse {
    version: string
    id: string
    result: {
        data: TransactionReportData[]
        sums: TransactionReportData[]
        rates: TransactionReportData[]
    }
}

export interface PostPaymentData {
    id: number
    deviceName: string
    ticket: string
    plate: string
    checkin: Date
    checkout: Date
    status: string
    cardType: string
    card: string
    amount: number
    discountType: string
    discountCode: string
}

export interface PostPaymentResponse {
    version: string
    id: string
    result: PostPaymentData[]
}

export interface AlarmsParams {
    location: number
    body: {
        Start: Date
        End: Date
        Devices: number[]
        ShowAllEvents: boolean
    }
}

export interface AlarmsData {
    deviceName: string
    unitOrTicket: string
    text: string
    type: number  // 0 = alarm, 1 = warning, 2 = check in, 3 = Coin, 4 = card post, 5 = card pre, 6 = mobilepay
    status: number // -1 = Deactivated, 0 = does not apply, 1 = Activated (can be deactivated)
    time: Date
    timeString: string
}

export interface AlarmsResponse {
    version: string
    id: string
    result: AlarmsData[]
}

export class AlarmsResponse implements AlarmsResponse {
    constructor(version: string = "", id: string = "", result: AlarmsData[] = []) {
        this.version = version
        this.id = id
        this.result = result
    }
}

export interface ParkantParams {
    location: number
    body: {
        Start: Date
        End: Date
        Devices: number[]
        ShowAllEvents: boolean
    }
}

export interface LicenseResponse {
    version: string
    id: string
    result: LicenseData[]
}

export class LicenseResponse implements LicenseResponse {
    constructor(version: string = "", id: string = "", devices: DeviceData[] = []) {
        this.version = version
        this.id = id
        this.result = devices
    }
}

export interface LicenseData {
    id: number
    name: string
}


export interface AreaEntry {
    id: number
    area: string
}

export interface StatusEntry {
    id: number
    status: string
    statusType: number
}

export interface DigitalLicensesStatsParams {
    requestType: string // Should be 'requests' or 'invoicing'. Default is requests
    start: Date
    end: Date
    licenseType: number
    areas: AreaEntry[]
    status: StatusEntry[]
}

export interface DigitalLicensesStatsData {
    id: number
    customerId: number
    licenseType: number
    period: number
    count: number
    startDate: Date
    startDateString: string
    endDate: Date
    endDateString: string
    paymentDate: Date
    area: string
    name: string
    address: string
    zip: string
    city: string
    companyName: string
    cvr: string
    plate: string
    plateCount: number
    price100: number
    priceString: string
    status: number
    acceptedDate: Date
    acceptedDateString: string
    reminderSent: boolean

    modifiedLicenseType: number
    modifiedPeriod: number
    modifiedCount: number
    modifiedStartDate: Date
    modifiedArea: string
    modifiedPlate: string
}

export interface DigitalLicensesStatsResponse {
    version: string
    id: string
    result: DigitalLicensesStatsData[]
}

export class DigitalLicensesStatsResponse implements DigitalLicensesStatsResponse {
    constructor(version: string = "", id: string = "", result: DigitalLicensesStatsData[] = []) {
        this.version = version
        this.id = id
        this.result = result
    }
}

export interface DigitalLicensesData {
    digitalLicenseRequestId: number // This is the ref number 
    licenseType: number
    period: number
    count: number
    startDate: Date
    endDate: Date
    area: string
    name: string
    address: string
    city: string
    zip: string
    email: string
    phone: string
    note: string
    parkantNote: string
    internalNote: string
    plate: string
    companyName: string
    cvr: string
    price100: number
    paymentDate: Date
    status: number

    modifiedLicenseType: number,
    modifiedPeriod: number,
    modifiedCount: number,
    modifiedStartDate: Date,
    modifiedArea: string,
    modifiedPlate: string,
}

export interface DigitalLicensesResponse {
    version: string
    id: string
    result: DigitalLicensesData[]
}

export class DigitalLicensesResponse implements DigitalLicensesResponse {
    constructor(version: string = "", id: string = "", result: DigitalLicensesData[] = []) {
        this.version = version
        this.id = id
        this.result = result
    }
}
